import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle,
  Send,
  MapPin,
  Users,
  Radio,
  AlertTriangle,
  CheckCircle,
  Clock,
  Phone,
  Video,
  MoreVertical,
  UserPlus,
  Image,
  Mic,
  Paperclip,
  Smile,
  Shield,
  Heart,
  Target
} from 'lucide-react';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  status: 'online' | 'offline' | 'responding';
  avatar?: string;
  location?: string;
  team: 'Alpha' | 'Bravo' | 'Charlie' | 'Delta';
  lastSeen?: string;
}

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: string;
  type: 'text' | 'location' | 'image' | 'emergency' | 'alert';
  readBy: string[];
  attachments?: string[];
  location?: { lat: number; lng: number };
}

interface Team {
  id: string;
  name: string;
  members: TeamMember[];
  channel: string;
  activeMission?: string;
}

const TeamChat: React.FC = () => {
  const [activeTeam, setActiveTeam] = useState<string>('Alpha');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      senderId: 'cmdr1',
      senderName: 'Commander Chen',
      content: 'Alpha team, what\'s your status at Zone A?',
      timestamp: '10:47 AM',
      type: 'text',
      readBy: ['mem1', 'mem2']
    },
    {
      id: '2',
      senderId: 'mem1',
      senderName: 'Lt. Rodriguez',
      content: 'Arrived at Zone A. Severe flooding, 47 people trapped on rooftops. Need boat support urgently!',
      timestamp: '10:48 AM',
      type: 'emergency',
      readBy: ['cmdr1']
    },
    {
      id: '3',
      senderId: 'cmdr1',
      senderName: 'Commander Chen',
      content: 'Copy that. Dispatching 2 boats to your location. ETA 5 minutes.',
      timestamp: '10:49 AM',
      type: 'text',
      readBy: ['mem1', 'mem2']
    },
    {
      id: '4',
      senderId: 'mem2',
      senderName: 'Medic Wong',
      content: 'Medical supplies running low. Need additional kits.',
      timestamp: '10:50 AM',
      type: 'alert',
      readBy: ['cmdr1']
    },
    {
      id: '5',
      senderId: 'mem1',
      senderName: 'Lt. Rodriguez',
      content: '📍 Current location: Zone A, Sector 7',
      timestamp: '10:51 AM',
      type: 'location',
      readBy: ['cmdr1', 'mem2']
    }
  ]);

  const [teams] = useState<Team[]>([
    {
      id: 'Alpha',
      name: 'Alpha Team',
      channel: 'alpha-command',
      activeMission: 'Zone A Flood Rescue',
      members: [
        { id: 'cmdr1', name: 'Commander Chen', role: 'Team Lead', status: 'online', team: 'Alpha', location: 'Command Center' },
        { id: 'mem1', name: 'Lt. Rodriguez', role: 'Rescue Specialist', status: 'responding', team: 'Alpha', location: 'Zone A' },
        { id: 'mem2', name: 'Medic Wong', role: 'Medical Officer', status: 'responding', team: 'Alpha', location: 'Zone A' },
        { id: 'mem3', name: 'Tech Davis', role: 'Communications', status: 'online', team: 'Alpha', location: 'Command Center' },
      ]
    },
    {
      id: 'Bravo',
      name: 'Bravo Team',
      channel: 'bravo-command',
      activeMission: 'Zone B Fire Suppression',
      members: [
        { id: 'cmdr2', name: 'Commander Patel', role: 'Team Lead', status: 'online', team: 'Bravo', location: 'Command Center' },
        { id: 'mem4', name: 'Capt. Martinez', role: 'Fire Specialist', status: 'responding', team: 'Bravo', location: 'Zone B' },
        { id: 'mem5', name: 'EMT Johnson', role: 'Medical', status: 'online', team: 'Bravo', location: 'En Route' },
      ]
    },
    {
      id: 'Charlie',
      name: 'Charlie Team',
      channel: 'charlie-command',
      activeMission: 'Zone C Building Collapse',
      members: [
        { id: 'cmdr3', name: 'Commander Kim', role: 'Team Lead', status: 'online', team: 'Charlie', location: 'Command Center' },
        { id: 'mem6', name: 'Sgt. Williams', role: 'Search & Rescue', status: 'responding', team: 'Charlie', location: 'Zone C' },
        { id: 'mem7', name: 'Dr. Gupta', role: 'Medical', status: 'responding', team: 'Charlie', location: 'Zone C' },
        { id: 'mem8', name: 'Eng. Sato', role: 'Structural Engineer', status: 'online', team: 'Charlie', location: 'En Route' },
      ]
    },
    {
      id: 'Delta',
      name: 'Delta Team',
      channel: 'delta-command',
      activeMission: 'Standby - Rapid Response',
      members: [
        { id: 'cmdr4', name: 'Commander Singh', role: 'Team Lead', status: 'online', team: 'Delta', location: 'Command Center' },
        { id: 'mem9', name: 'Spec. Cooper', role: 'Tactical', status: 'online', team: 'Delta', location: 'Base' },
        { id: 'mem10', name: 'Medic Foster', role: 'Medical', status: 'offline', team: 'Delta', lastSeen: '5 min ago' },
      ]
    }
  ]);

  const [newMessage, setNewMessage] = useState('');
  const [showEmergencyPanel, setShowEmergencyPanel] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  const activeTeamData = teams.find(t => t.id === activeTeam);

  const sendMessage = () => {
    if (!newMessage.trim()) return;
    
    const message: Message = {
      id: Date.now().toString(),
      senderId: 'cmdr1',
      senderName: 'Commander Chen',
      content: newMessage,
      timestamp: 'Just now',
      type: 'text',
      readBy: []
    };
    
    setMessages([...messages, message]);
    setNewMessage('');
  };

  const sendEmergencyAlert = () => {
    const emergencyMsg: Message = {
      id: Date.now().toString(),
      senderId: 'cmdr1',
      senderName: 'Commander Chen',
      content: '🚨 EMERGENCY BROADCAST: All teams prepare for immediate evacuation of Zone A!',
      timestamp: 'Just now',
      type: 'emergency',
      readBy: []
    };
    setMessages([...messages, emergencyMsg]);
    setShowEmergencyPanel(false);
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'online': return 'text-green-500 border-green-500';
      case 'responding': return 'text-yellow-500 border-yellow-500 animate-pulse';
      case 'offline': return 'text-gray-500 border-gray-500';
      default: return 'text-gray-500';
    }
  };

  const getMessageTypeIcon = (type: string) => {
    switch(type) {
      case 'emergency': return <AlertTriangle size={14} className="text-red-500" />;
      case 'alert': return <Radio size={14} className="text-yellow-500" />;
      case 'location': return <MapPin size={14} className="text-cyan-500" />;
      default: return null;
    }
  };

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 holographic-card h-[600px] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Radio className="text-green-500 animate-pulse" />
            RESCUE TEAM COMMUNICATION
          </h2>
          <p className="text-xs text-gray-400 mt-1">Real-time coordination • Encrypted channel</p>
        </div>

        {/* Emergency Broadcast Button */}
        <button
          onClick={() => setShowEmergencyPanel(true)}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
        >
          <AlertTriangle size={16} />
          EMERGENCY
        </button>
      </div>

      {/* Team Selection Tabs */}
      <div className="flex gap-2 mb-4 border-b border-gray-700/50 pb-2">
        {teams.map((team) => (
          <button
            key={team.id}
            onClick={() => setActiveTeam(team.id)}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
              activeTeam === team.id
                ? 'bg-cyan-600 text-white'
                : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {team.name}
          </button>
        ))}
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Team Members Sidebar */}
        <div className="w-64 bg-black/30 rounded-lg p-3 overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Users size={14} className="text-cyan-500" />
              Team Members
            </h3>
            <span className="text-xs text-gray-400">
              {activeTeamData?.members.filter(m => m.status !== 'offline').length} online
            </span>
          </div>

          <div className="space-y-2">
            {activeTeamData?.members.map((member) => (
              <motion.div
                key={member.id}
                whileHover={{ scale: 1.02 }}
                className={`p-2 rounded-lg cursor-pointer ${
                  selectedMember?.id === member.id ? 'bg-cyan-600/20' : 'hover:bg-gray-800/50'
                }`}
                onClick={() => setSelectedMember(member)}
              >
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <div className="w-8 h-8 bg-gradient-to-br from-cyan-600 to-blue-600 rounded-full flex items-center justify-center text-sm font-bold">
                      {member.name.charAt(0)}
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-gray-900 ${
                      member.status === 'online' ? 'bg-green-500' :
                      member.status === 'responding' ? 'bg-yellow-500 animate-pulse' : 'bg-gray-500'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{member.name}</p>
                    <p className="text-xs text-gray-400">{member.role}</p>
                  </div>
                  {member.status === 'responding' && (
                    <Target size={12} className="text-yellow-500 animate-pulse" />
                  )}
                </div>
                {member.location && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <MapPin size={10} />
                    {member.location}
                  </p>
                )}
              </motion.div>
            ))}
          </div>

          {/* Active Mission */}
          {activeTeamData?.activeMission && (
            <div className="mt-4 p-3 bg-cyan-600/10 rounded-lg border border-cyan-500/30">
              <p className="text-xs text-gray-400 mb-1">ACTIVE MISSION</p>
              <p className="text-sm font-bold">{activeTeamData.activeMission}</p>
            </div>
          )}
        </div>

        {/* Chat Messages */}
        <div className="flex-1 bg-black/30 rounded-lg p-3 flex flex-col">
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {messages.map((msg, idx) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.senderId === 'cmdr1' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${
                  msg.type === 'emergency' ? 'bg-red-600/20 border-red-500/30' :
                  msg.type === 'alert' ? 'bg-yellow-600/20 border-yellow-500/30' :
                  msg.senderId === 'cmdr1' ? 'bg-cyan-600/20 border-cyan-500/30' : 'bg-gray-800/50 border-gray-700'
                } border rounded-lg p-3`}>
                  {msg.senderId !== 'cmdr1' && (
                    <p className="text-xs font-bold text-cyan-500 mb-1">{msg.senderName}</p>
                  )}
                  <div className="flex items-start gap-2">
                    {getMessageTypeIcon(msg.type)}
                    <p className="text-sm">{msg.content}</p>
                  </div>
                  <div className="flex items-center justify-end gap-2 mt-1">
                    <span className="text-xs text-gray-400">{msg.timestamp}</span>
                    {msg.readBy.length > 0 && (
                      <div className="flex -space-x-1">
                        {msg.readBy.slice(0, 3).map((_, i) => (
                          <div key={i} className="w-4 h-4 bg-gray-600 rounded-full border border-gray-700 text-[8px] flex items-center justify-center">
                            ✓
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Message Input */}
          <div className="mt-3 flex gap-2">
            <button className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700">
              <Paperclip size={18} className="text-gray-400" />
            </button>
            <button className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700">
              <Image size={18} className="text-gray-400" />
            </button>
            <button className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700">
              <Mic size={18} className="text-gray-400" />
            </button>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type your message..."
              className="flex-1 bg-black/50 border border-gray-700 rounded-lg px-4 py-2 text-sm"
            />
            <button
              onClick={sendMessage}
              className="p-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Emergency Broadcast Modal */}
      <AnimatePresence>
        {showEmergencyPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setShowEmergencyPanel(false)}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="bg-gray-900 w-96 p-6 rounded-xl border border-red-500/50"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-red-500 mb-4 flex items-center gap-2">
                <AlertTriangle size={24} />
                EMERGENCY BROADCAST
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                This will send an urgent alert to ALL rescue teams immediately.
              </p>
              <textarea
                placeholder="Enter emergency message..."
                className="w-full bg-black/50 border border-red-500/30 rounded-lg p-3 mb-4 text-sm"
                rows={3}
                defaultValue="🚨 EMERGENCY: All teams prepare for immediate evacuation of Zone A!"
              />
              <div className="flex gap-3">
                <button
                  onClick={sendEmergencyAlert}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg"
                >
                  SEND ALERT
                </button>
                <button
                  onClick={() => setShowEmergencyPanel(false)}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 rounded-lg"
                >
                  CANCEL
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Member Details Modal */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setSelectedMember(null)}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="bg-gray-900 w-80 p-6 rounded-xl border border-cyan-500/50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-600 to-blue-600 rounded-full flex items-center justify-center text-xl font-bold">
                  {selectedMember.name.charAt(0)}
                </div>
                <div>
                  <h3 className="font-bold">{selectedMember.name}</h3>
                  <p className="text-sm text-gray-400">{selectedMember.role}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="p-3 bg-black/30 rounded-lg">
                  <p className="text-xs text-gray-400">Status</p>
                  <p className={`text-sm font-bold ${getStatusColor(selectedMember.status)}`}>
                    {selectedMember.status.toUpperCase()}
                  </p>
                </div>
                
                <div className="p-3 bg-black/30 rounded-lg">
                  <p className="text-xs text-gray-400">Location</p>
                  <p className="text-sm font-bold">{selectedMember.location || 'Unknown'}</p>
                </div>
                
                <div className="p-3 bg-black/30 rounded-lg">
                  <p className="text-xs text-gray-400">Team</p>
                  <p className="text-sm font-bold">{selectedMember.team}</p>
                </div>
                
                {selectedMember.lastSeen && (
                  <div className="p-3 bg-black/30 rounded-lg">
                    <p className="text-xs text-gray-400">Last Seen</p>
                    <p className="text-sm font-bold">{selectedMember.lastSeen}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <button className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white py-2 rounded-lg text-sm flex items-center justify-center gap-1">
                  <MessageCircle size={14} />
                  Message
                </button>
                <button className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg text-sm flex items-center justify-center gap-1">
                  <Phone size={14} />
                  Call
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeamChat;